// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { RiskFlag, RiskResult } from '../../api/profileFormApi';

/**
 * Read-only render of a risk-attitude score: the 1–7 attitude level plus the
 * Signals & flags list (the fixed "Experience and Timeline" methodology — see
 * the Resource Server's RiskAttitudeScorer).
 */

const SEVERITY: Record<string, { box: string; dot: string; label: string }> = {
  ok: { box: 'bg-[#ecfdf5] border-[#a7f3d0] text-[#065f46]', dot: 'bg-[#10b981]', label: 'OK' },
  info: { box: 'bg-[#eff6ff] border-[#bfdbfe] text-[#1e40af]', dot: 'bg-[#3b82f6]', label: 'Info' },
  warn: { box: 'bg-[#fffbeb] border-[#fde68a] text-[#92400e]', dot: 'bg-[#f59e0b]', label: 'Attention' },
  alert: { box: 'bg-[#fef2f2] border-[#fecaca] text-[#991b1b]', dot: 'bg-[#ef4444]', label: 'Alert' },
};

const LEVEL_LABELS = ['', 'Very low', 'Low', 'Cautious', 'Balanced', 'Growth', 'High', 'Very high'];

function styleFor(severity: string) {
  return SEVERITY[(severity || '').toLowerCase()] || SEVERITY.info;
}

export function RiskResultCard({ result }: Readonly<{ result: RiskResult }>) {
  const level = Math.min(Math.max(result.level || 1, 1), 7);
  const flags: RiskFlag[] = result.flags || [];

  return (
    <div data-testid="advisor-risk-result" className="mb-4">
      <div className="flex items-center gap-4 p-4 rounded-button bg-[#faf5ff] border border-[#E1E1E1] mb-4">
        <div className="flex flex-col items-center justify-center w-16 h-16 rounded-full bg-[#573B89] text-white shrink-0">
          <span className="text-h5 font-bold leading-none" data-testid="advisor-risk-level">{level}</span>
          <span className="text-[11px] opacity-85">/ 7</span>
        </div>
        <div>
          <div className="text-[13px] text-[#6b7280]">Attitude to risk</div>
          <div className="text-[18px] font-semibold">{LEVEL_LABELS[level]}</div>
          <div className="text-body-04 text-[#6b7280]">Raw score {result.raw}</div>
        </div>
      </div>

      {flags.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-[13px] font-semibold text-[#6b7280]">Signals &amp; flags</div>
          {flags.map((f) => {
            const s = styleFor(f.severity);
            return (
              <div
                key={f.key}
                data-testid={`advisor-risk-flag-${f.key}`}
                className={`flex items-start gap-[10px] px-3 py-[10px] rounded-lg border ${s.box}`}
              >
                <span className={`w-[10px] h-[10px] rounded-full mt-[5px] shrink-0 ${s.dot}`} aria-hidden />
                <div>
                  <span className="text-[11px] uppercase tracking-wide opacity-80">{s.label}</span>
                  <div className="text-body-03">{f.message}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
